import * as Yup from 'yup';
import type { TFunction } from 'i18next';

import type {
    Ad,
    AddressParam,
    CommonAreasState,
    HomadataQuery,
    Maybe,
    PropertyPurposeEstimation,
    PropertyResidenceType,
    PropertyState,
    PropertyTypes,
} from 'src/types/__generated__/graphql';
import { AdStatuses, AdTypes, YesNoOptions } from 'src/types/__generated__/graphql';
import { formatFloorPosition } from 'src/utils/adUtils';
import isNotNull from 'src/utils/isNotNull';
import isObjectUndefinedOrEmpty from 'src/utils/isObjectUndefinedOrEmpty';

const ifNumber = (x: unknown) => (x ? Number(x) : undefined);

export interface EstimateAdFormValues {
    id?: string;
    addressSearch?: string;
    address?: AddressParam;
    isHiddingCompleteAddress?: boolean;
    propertyType?: PropertyTypes;
    constructionYear?: number;
    floorPosition?: number;
    floorNumber?: number;
    livingArea?: number;
    landArea?: number;
    roomNumber?: number;
    bedroomNumber?: number;
    assets: Record<string, boolean>;
    heatType?: Ad['heatType'];
    consumption?: string | null;
    emissions?: string | null;
    ongoingConsumption?: boolean;
    ongoingEmissions?: boolean;
    pricePerSquareFeet?: number;
    newProperty?: boolean;
    surfaceArea?: number;
    createdAt?: string;
    userId?: string;
    companyId?: string;
    propertyState?: PropertyState;
    commonAreasState?: CommonAreasState;
    propertyPurposeEstimation?: PropertyPurposeEstimation;
    propertyResidenceType?: PropertyResidenceType;
    userIsOwner?: YesNoOptions;
    contactConsent?: boolean;
}

export const estimateSchema = (
    t: TFunction<Array<'create-ad' | 'common'>, undefined, Array<'create-ad' | 'common'>>,
    step: number,
) => {
    const stepSchemas: Yup.ObjectShape[] = [
        {
            isHiddingCompleteAddress: Yup.boolean().required(t('errors.required')),
        },
        {
            propertyType: Yup.string().required(t('common:errors.required')),
        },
        {
            constructionYear: Yup.number()
                .nullable()
                .when('propertyType', {
                    is: (propertyType: PropertyTypes) => !['parking', 'land'].includes(String(propertyType)),
                    then: () =>
                        Yup.number()
                            .required(t('common:errors:required'))
                            .min(1000, t('common:errors.yearFormat'))
                            .max(9999, t('common:errors.yearFormat')),
                }),
            floorPosition: Yup.number().min(0).nullable(),
            floorNumber: Yup.number().min(1).nullable(),
            roomNumber: Yup.number().min(1).nullable(),
            bedroomNumber: Yup.number().min(0).nullable(),
        },
        {
            propertyState: Yup.string().required(t('common:errors.required')),
        },
        {},
        {
            heatType: Yup.string().required(t('common:errors.required')),
            consumption: Yup.string()
                .nullable()
                .when('ongoingConsumption', {
                    is: false,
                    then: () => Yup.string().required(t('common:errors.required')),
                }),
            emissions: Yup.string()
                .nullable()
                .when('ongoingEmissions', {
                    is: false,
                    then: () => Yup.string().required(t('common:errors.required')),
                }),
        },
        {
            propertyPurposeEstimation: Yup.string().required(t('common:errors.required')),
            propertyResidenceType: Yup.string().required(t('common:errors.required')),
            userIsOwner: Yup.string().required(t('common:errors.required')),
        },
    ];
    // Schemas for current step
    const schemaForCurrentStep = stepSchemas[step - 1];
    return Yup.object().shape(schemaForCurrentStep);
};

export const getHomaDataVariables = (values: EstimateAdFormValues) => ({
    variables: {
        isOnEstimationFlow: true,
        lng: Number(values.address?.lng),
        lat: Number(values.address?.lat),
        propertyType: values.propertyType as PropertyTypes,
        livingArea: Number(values.livingArea),
        landArea: Number(values.landArea),
        roomNumber: Number(values.roomNumber),
        bedroomNumber: Number(values.bedroomNumber),
        floorPosition: Number(values.floorPosition),
        floorNumber: Number(values.floorNumber),
        constructionYear: Number(values.constructionYear),
    },
});

export const getEstimateAdVariables = (
    values: EstimateAdFormValues,
    newHomadata: HomadataQuery | undefined,
    existingAd: Ad,
    currentUserId?: string,
) => ({
    userId: currentUserId,
    type: AdTypes.Sell,
    isHiddingCompleteAddress: Boolean(values.isHiddingCompleteAddress),
    propertyType: values.propertyType as PropertyTypes,
    constructionYear: ifNumber(values.constructionYear),
    media: [],
    title: '',
    description: '',
    interOffice: false,
    floorPosition: formatFloorPosition({
        floorPosition: values.floorPosition,
        propertyType: values.propertyType,
    }),
    livingArea: ifNumber(values.livingArea),
    landArea: ifNumber(values.landArea),
    googlePlaceId: String(values.address?.placeId),
    address: !isObjectUndefinedOrEmpty(values.address as unknown as Record<string, unknown>)
        ? values.address
        : undefined,
    cityName: String(values.address?.city),
    zipCode: String(values.address?.zipCode),
    bedroomNumber: ifNumber(values.bedroomNumber),
    roomNumber: ifNumber(values.roomNumber),
    floorNumber: ifNumber(values.floorNumber),
    heatType: values.heatType,
    consumption: values.consumption ? values.consumption : null,
    emissions: values.emissions ? values.emissions : null,
    assets: Object.keys(values.assets),
    newProperty: values.newProperty,
    surfaceArea: ifNumber(values.surfaceArea),
    homadataEvaluation: JSON.stringify({
        confidenceMin: Math.floor(
            newHomadata?.homadata?.confidenceMin ?? existingAd.homadataEvaluation?.confidenceMin ?? 0,
        ),
        confidenceMax: Math.floor(
            newHomadata?.homadata?.confidenceMax ?? existingAd.homadataEvaluation?.confidenceMax ?? 0,
        ),
        predictedPrice: Math.floor(
            newHomadata?.homadata?.predictedPrice ?? existingAd.homadataEvaluation?.predictedPrice ?? 0,
        ),
        confidenceIndex: Math.floor(
            newHomadata?.homadata?.confidenceIndex ?? existingAd.homadataEvaluation?.confidenceIndex ?? 0,
        ),
        requestDate: Math.floor(Date.now() / 1000),
    }),
    companyId: values.companyId,
    propertyState: values.propertyState,
    commonAreasState: values.commonAreasState,
    propertyPurposeEstimation: values.propertyPurposeEstimation,
    propertyResidenceType: values.propertyResidenceType,
    userIsOwner: values.userIsOwner ? values.userIsOwner === YesNoOptions.Yes : undefined,
    contactConsent: values.contactConsent,
    status: AdStatuses.Draft,
    isInaccurateAddress: false,
});

export const getInitialEstimateAdFormValues = (existingAd: Maybe<Ad>) => ({
    ...existingAd,
    address: existingAd?.address
        ? ({
              description: existingAd.address.description,
              googlePlaceId: existingAd.address.placeId,
              lat: existingAd.proxyLat,
              lng: existingAd.proxyLng,
              zipCode: existingAd.address.zipCode,
              cityName: existingAd.address.cityName,
          } as EstimateAdFormValues['address'])
        : undefined,
    propertyType: existingAd?.propertyType as EstimateAdFormValues['propertyType'],
    constructionYear: ifNumber(existingAd?.constructionYear),
    floorPosition: formatFloorPosition({
        floorPosition: existingAd?.floorPosition,
        propertyType: existingAd?.propertyType,
    }),
    floorNumber: ifNumber(existingAd?.floorNumber),
    livingArea: ifNumber(existingAd?.livingArea),
    landArea: ifNumber(existingAd?.landArea),
    surfaceArea: ifNumber(existingAd?.surfaceArea),
    roomNumber: ifNumber(existingAd?.roomNumber),
    bedroomNumber: ifNumber(existingAd?.bedroomNumber),
    consumption: existingAd?.consumption !== undefined ? existingAd.consumption : 'A',
    ongoingConsumption: existingAd?.consumption === null,
    emissions: existingAd?.emissions !== undefined ? existingAd.emissions : 'A',
    ongoingEmissions: existingAd?.emissions === null,
    isHiddingCompleteAddress: Boolean(existingAd?.isHiddingCompleteAddress),
    assets: existingAd?.assets
        ? existingAd.assets.reduce(
              (obj, curr) => ({
                  ...obj,
                  [String(curr)]: true,
              }),
              {},
          )
        : {},
    propertyState: existingAd?.propertyState ? existingAd.propertyState : undefined,
    commonAreasState: existingAd?.commonAreasState ? existingAd.commonAreasState : undefined,
    propertyPurposeEstimation: existingAd?.propertyPurposeEstimation ? existingAd.propertyPurposeEstimation : undefined,
    propertyResidenceType: existingAd?.propertyResidenceType ? existingAd.propertyResidenceType : undefined,
    // eslint-disable-next-line no-nested-ternary
    userIsOwner: isNotNull(existingAd?.userIsOwner)
        ? existingAd?.userIsOwner
            ? YesNoOptions.Yes
            : YesNoOptions.No
        : undefined,
    contactConsent: existingAd?.contactConsent ? existingAd.contactConsent : undefined,
});
